import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  cn,
} from '@yeshuman/sdk'
import { useEffect, useMemo, useState } from 'react'
import { CalendarCheck, PawPrint, Phone, Search } from 'lucide-react'
import { toast } from 'sonner'

import {
  useAnimalsForContact,
  useAppointmentAvailability,
  useAppointmentResources,
  useAppointmentTypes,
  useBookAppointment,
  useContactLookup,
  type AppointmentBooking,
  type AvailabilitySlot,
} from '../hooks/use-call-outcomes'

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10)
}

function slotTimeLabel(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso))
}

function SectionLabel({
  step,
  children,
}: {
  step: number
  children: React.ReactNode
}) {
  return (
    <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
      <span className="flex size-5 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
        {step}
      </span>
      {children}
    </h3>
  )
}

export function BookingWorkbench({
  callRecordId,
  callerNumber,
  callerName,
  existingBooking = null,
}: {
  callRecordId: string
  callerNumber: string
  callerName: string
  /** When set, collapse create form until the operator chooses to book another. */
  existingBooking?: AppointmentBooking | null
}) {
  const [phone, setPhone] = useState(callerNumber)
  const [lookupPhone, setLookupPhone] = useState('')
  const [contactId, setContactId] = useState('')
  const [animalId, setAnimalId] = useState('')
  const [appointmentTypeUid, setAppointmentTypeUid] = useState('')
  const [resourceUid, setResourceUid] = useState('')
  const [date, setDate] = useState(todayIsoDate())
  const [selectedSlot, setSelectedSlot] = useState<AvailabilitySlot | null>(null)
  const [notes, setNotes] = useState('')
  const [booking, setBooking] = useState<AppointmentBooking | null>(null)
  const [forceCreate, setForceCreate] = useState(false)

  const collapsedBooking = booking ?? (!forceCreate ? existingBooking : null)

  const lookup = useContactLookup(lookupPhone)
  const contacts = useMemo(() => lookup.data?.contacts ?? [], [lookup.data])
  const contact = contacts.find((c) => c.external_id === contactId) ?? null

  const animalsQuery = useAnimalsForContact(contactId)
  const animals = animalsQuery.data ?? []
  const animal = animals.find((a) => a.external_id === animalId) ?? null

  const { data: appointmentTypes } = useAppointmentTypes()
  const { data: resources } = useAppointmentResources()
  const appointmentType =
    appointmentTypes?.find((t) => t.uid === appointmentTypeUid) ?? null

  const durationMinutes = appointmentType?.default_duration_minutes ?? 30

  const availabilityQuery = useMemo(() => {
    if (!appointmentTypeUid || !resourceUid || !date) return null
    return {
      appointment_type_uid: appointmentTypeUid,
      resource_uid: resourceUid,
      start_at: `${date}T00:00:00Z`,
      end_at: `${date}T23:59:59Z`,
      duration_minutes: durationMinutes,
    }
  }, [appointmentTypeUid, resourceUid, date, durationMinutes])

  const availability = useAppointmentAvailability(availabilityQuery)
  const slots = availability.data ?? []

  const bookAppointment = useBookAppointment()

  // Auto-select when the lookup returns exactly one contact.
  useEffect(() => {
    if (contacts.length === 1 && !contactId) {
      setContactId(contacts[0].external_id)
    }
  }, [contacts, contactId])

  useEffect(() => {
    setSelectedSlot(null)
  }, [appointmentTypeUid, resourceUid, date])

  function handleLookup() {
    setContactId('')
    setAnimalId('')
    setLookupPhone(phone)
  }

  function handleBook() {
    if (!contact || !animal || !selectedSlot) return
    bookAppointment.mutate(
      {
        call_record_id: callRecordId,
        animal_external_id: animal.external_id,
        contact_external_id: contact.external_id,
        appointment_type_uid: selectedSlot.appointment_type_uid,
        resource_uid: selectedSlot.resource_uid,
        start_at: selectedSlot.start_at,
        duration_minutes: selectedSlot.duration_minutes,
        patient_name: animal.name,
        contact_name: contact.name || callerName,
        notes,
        created_source: 'staff',
      },
      {
        onSuccess: (row) => {
          setBooking(row)
          toast.success('Appointment booked')
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Booking failed')
        },
      }
    )
  }

  // Session-just-booked: full confirmation (context summary may still be refreshing).
  if (booking) {
    return (
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle className="flex items-center gap-2">
            <CalendarCheck className="h-4 w-4" aria-hidden />
            Booking workbench
          </CardTitle>
          <Badge variant="successSoft">Booked</Badge>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p className="text-foreground">
            {booking.appointment_type_name || 'Appointment'} for{' '}
            <span className="font-medium">{animal?.name || 'pet'}</span> with{' '}
            <span className="font-medium">{booking.resource_name}</span>
          </p>
          <p className="text-muted-foreground">
            {slotTimeLabel(booking.start_at)} · {booking.duration_minutes} min ·{' '}
            {booking.provider} ({booking.sync_status})
          </p>
          {booking.external_appointment_uid ? (
            <p className="font-mono text-xs text-muted-foreground">
              {booking.external_appointment_uid}
            </p>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setBooking(null)
              setForceCreate(true)
            }}
          >
            Book another appointment
          </Button>
        </CardContent>
      </Card>
    )
  }

  // Existing booking on the call: details live in CallBookingSummary; only offer another book.
  if (collapsedBooking) {
    return (
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle className="flex items-center gap-2">
            <CalendarCheck className="h-4 w-4" aria-hidden />
            Booking workbench
          </CardTitle>
          <Badge variant="successSoft">Booked</Badge>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setForceCreate(true)}
          >
            Book another appointment
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarCheck className="h-4 w-4" aria-hidden />
          Booking workbench
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <section className="space-y-3">
          <SectionLabel step={1}>Find the caller</SectionLabel>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="workbench-phone">Phone</Label>
              <Input
                id="workbench-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Caller phone number"
                className="w-56"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleLookup}
              disabled={!phone.trim() || lookup.isFetching}
            >
              <Search className="h-3.5 w-3.5 mr-1" />
              {lookup.isFetching ? 'Looking up…' : 'Look up'}
            </Button>
          </div>
          {lookup.isError ? (
            <p className="text-sm text-destructive">
              {(lookup.error as Error).message}
            </p>
          ) : null}
          {lookup.data && !lookup.data.found ? (
            <p className="text-sm text-muted-foreground">
              {lookup.data.message ||
                'No contact found for this phone number. Offer a callback instead of booking.'}
            </p>
          ) : null}
          {contacts.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {contacts.map((c) => (
                <Button
                  key={c.external_id}
                  type="button"
                  size="sm"
                  variant={c.external_id === contactId ? 'default' : 'outline'}
                  onClick={() => {
                    setContactId(c.external_id)
                    setAnimalId('')
                  }}
                >
                  <Phone className="h-3.5 w-3.5 mr-1" />
                  {c.name || c.external_id}
                </Button>
              ))}
            </div>
          ) : null}
        </section>

        {contact ? (
          <section className="space-y-3">
            <SectionLabel step={2}>Pick a pet</SectionLabel>
            {animalsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading pets…</p>
            ) : animals.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No registered pets for {contact.name || 'this contact'}.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {animals.map((a) => (
                  <Button
                    key={a.external_id}
                    type="button"
                    size="sm"
                    variant={a.external_id === animalId ? 'default' : 'outline'}
                    onClick={() => setAnimalId(a.external_id)}
                  >
                    <PawPrint className="h-3.5 w-3.5 mr-1" />
                    {a.name}
                    {a.species ? ` (${a.species})` : ''}
                  </Button>
                ))}
              </div>
            )}
          </section>
        ) : null}

        {animal ? (
          <section className="space-y-3">
            <SectionLabel step={3}>Choose a slot</SectionLabel>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label>Appointment type</Label>
                <Select value={appointmentTypeUid} onValueChange={setAppointmentTypeUid}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {(appointmentTypes ?? [])
                      .filter((t) => t.bookable)
                      .map((t) => (
                        <SelectItem key={t.uid} value={t.uid}>
                          {t.name} ({t.default_duration_minutes} min)
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Resource</Label>
                <Select value={resourceUid} onValueChange={setResourceUid}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select resource" />
                  </SelectTrigger>
                  <SelectContent>
                    {(resources ?? [])
                      .filter((r) => r.bookable)
                      .map((r) => (
                        <SelectItem key={r.uid} value={r.uid}>
                          {r.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="workbench-date">Date</Label>
                <Input
                  id="workbench-date"
                  type="date"
                  value={date}
                  min={todayIsoDate()}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
            </div>
            {availabilityQuery ? (
              availability.isLoading ? (
                <p className="text-sm text-muted-foreground">Checking availability…</p>
              ) : availability.isError ? (
                <p className="text-sm text-destructive">
                  {(availability.error as Error).message}
                </p>
              ) : slots.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No open slots for this day — try another date or resource.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {slots.map((slot) => {
                    const selected = selectedSlot?.start_at === slot.start_at
                    return (
                      <Button
                        key={`${slot.resource_uid}-${slot.start_at}`}
                        type="button"
                        size="sm"
                        variant={selected ? 'default' : 'outline'}
                        className={cn('tabular-nums', !slot.bookable && 'opacity-50')}
                        disabled={!slot.bookable}
                        onClick={() => setSelectedSlot(slot)}
                      >
                        {slotTimeLabel(slot.start_at)}
                      </Button>
                    )
                  })}
                </div>
              )
            ) : (
              <p className="text-sm text-muted-foreground">
                Pick a type and resource to see open slots.
              </p>
            )}
          </section>
        ) : null}

        {selectedSlot && animal && contact ? (
          <section className="space-y-3">
            <SectionLabel step={4}>Confirm</SectionLabel>
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
              <span className="font-medium text-foreground">
                {selectedSlot.appointment_type_name}
              </span>{' '}
              for <span className="font-medium text-foreground">{animal.name}</span> with{' '}
              <span className="font-medium text-foreground">
                {selectedSlot.resource_name}
              </span>{' '}
              · {slotTimeLabel(selectedSlot.start_at)} · {selectedSlot.duration_minutes} min
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="workbench-notes">Notes (optional)</Label>
              <Textarea
                id="workbench-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Reason for visit, caller context…"
                rows={2}
              />
            </div>
            <Button
              type="button"
              onClick={handleBook}
              disabled={bookAppointment.isPending}
            >
              <CalendarCheck className="h-4 w-4 mr-1" />
              {bookAppointment.isPending ? 'Booking…' : 'Book appointment'}
            </Button>
          </section>
        ) : null}
      </CardContent>
    </Card>
  )
}
